/**
 * Social Media API
 */
import apiClient from './index'
import type { ApiResponse, PaginatedResponse, PaginationParams } from '@/types'

// Types
export interface SocialAccount {
  id: string
  platform: 'twitter' | 'linkedin' | 'facebook' | 'instagram'
  account_id: string
  account_name: string
  account_handle: string
  account_avatar?: string
  is_active: boolean
  created_at: string
}

export interface LinkedInOrganization {
  id: string
  urn: string
  name: string
  vanityName: string
  logoUrl?: string
}

export interface SocialPlatform {
  id: string
  name: string
  icon: string
  color: string
  max_chars: number
  supports_media: boolean
  media_types: string[]
  requires_media?: boolean
}

export interface SocialPostMetrics {
  likes: number
  comments: number
  shares: number
  impressions: number
  engagement_rate: number
}

export type PostStatus = 'draft' | 'pending_approval' | 'approved' | 'scheduled' | 'publishing' | 'published' | 'failed' | 'rejected'
export type ApprovalStatus = 'pending' | 'approved' | 'rejected' | 'changes_requested'
export type Priority = 'low' | 'medium' | 'high' | 'urgent'
export type PostCategory = 'blog_post' | 'social_media_post' | 'video' | 'help_article' | 'announcement' | 'promotion' | 'other'

export interface SocialPost {
  id: string
  content: string
  media_urls: string[]
  platforms: string[]
  social_account_ids: string[]
  status: PostStatus
  scheduled_at?: string
  published_at?: string
  error_message?: string
  platform_post_ids: Record<string, string>
  metrics: SocialPostMetrics
  contact_id?: string
  company_id?: string
  deal_id?: string
  tags: string[]
  created_by_user_id: string
  created_at: string
  updated_at?: string
  // Approval workflow fields
  category?: PostCategory
  priority?: Priority
  draft_due_date?: string
  approval_status?: ApprovalStatus
  approval_required?: boolean
  approver_user_id?: string
  approved_by_user_id?: string
  approved_at?: string
  approval_notes?: string
  progress?: number
}

export interface SocialPostCreate {
  content: string
  media_urls?: string[]
  platforms?: string[]
  social_account_ids?: string[]
  scheduled_at?: string
  contact_id?: string
  company_id?: string
  deal_id?: string
  tags?: string[]
}

export interface SocialPostUpdate {
  content?: string
  media_urls?: string[]
  platforms?: string[]
  social_account_ids?: string[]
  scheduled_at?: string | null
  tags?: string[]
}

export interface SocialTemplate {
  id: string
  name: string
  content: string
  platforms: string[]
  category?: string
  tags: string[]
  created_at: string
}

export interface SocialAnalytics {
  period_days: number
  total_posts: number
  total_likes: number
  total_comments: number
  total_shares: number
  total_impressions: number
  total_engagement: number
  avg_engagement_per_post: number
  by_platform: Record<string, { count: number; engagement: number }>
  by_day: Record<string, number>
}

export interface DailyTrend {
  date: string
  posts: number
  likes: number
  comments: number
  shares: number
  impressions: number
  engagement: number
}

export interface EngagementTrends {
  period_days: number
  trends: DailyTrend[]
}

export interface BestTime {
  hour?: number
  day?: number
  display?: string
  name?: string
  posts: number
  avg_engagement: number
}

export interface BestPostingTimes {
  platform: string | null
  period_days: number
  best_hours: BestTime[]
  best_days: BestTime[]
  all_hours: Array<{ posts: number; engagement: number }>
}

export interface QueuedPost extends SocialPost {
  queue_position: number
  scheduled_at: string  // Override to make required for queued posts
}

export interface PostFilters {
  status?: string
  platform?: string
  start_date?: string
  end_date?: string
}

export interface GeneratePostRequest {
  topic: string
  tone?: 'professional' | 'casual' | 'friendly' | 'formal'
  platform?: string
  include_hashtags?: boolean
  include_emoji?: boolean
}

export interface ImprovePostRequest {
  content: string
  platform?: string
  type?: 'engagement' | 'clarity' | 'brevity' | 'hashtags'
}

export const socialApi = {
  // Accounts
  async listAccounts(): Promise<SocialAccount[]> {
    const response = await apiClient.get<ApiResponse<SocialAccount[]>>('/social/accounts')
    return response.data.data
  },

  async getAccount(accountId: string): Promise<SocialAccount> {
    const response = await apiClient.get<ApiResponse<SocialAccount>>(`/social/accounts/${accountId}`)
    return response.data.data
  },

  async disconnectAccount(accountId: string): Promise<void> {
    await apiClient.post(`/social/accounts/${accountId}/disconnect`)
  },

  /**
   * Initiate OAuth flow for connecting a social media account.
   * Returns the authorization URL to redirect the user to.
   * @param platform - The platform to connect ('twitter', 'linkedin', 'facebook', 'instagram')
   */
  async getOAuthUrl(platform: string): Promise<{ authorization_url: string }> {
    const response = await apiClient.get<ApiResponse<{ authorization_url: string }>>(
      `/social/oauth/${platform}/authorize`
    )
    return response.data.data
  },

  /** Disconnect the current user's OAuth connection for a platform. */
  async disconnectOAuth(platform: string): Promise<void> {
    await apiClient.post(`/social/oauth/${platform}/disconnect`)
  },

  /**
   * Get LinkedIn organizations (company pages) the user can post to.
   * Only works for LinkedIn accounts.
   */
  async getLinkedInOrganizations(accountId: string): Promise<LinkedInOrganization[]> {
    const response = await apiClient.get<ApiResponse<LinkedInOrganization[]>>(
      `/social/accounts/${accountId}/organizations`
    )
    return response.data.data
  },

  async getPlatforms(): Promise<SocialPlatform[]> {
    const response = await apiClient.get<ApiResponse<SocialPlatform[]>>('/social/platforms')
    return response.data.data
  },

  // Posts
  async listPosts(params?: PaginationParams & PostFilters): Promise<PaginatedResponse<SocialPost>> {
    const response = await apiClient.get<PaginatedResponse<SocialPost>>('/social/posts', { params })
    return response.data
  },

  async getPost(postId: string): Promise<SocialPost> {
    const response = await apiClient.get<ApiResponse<SocialPost>>(`/social/posts/${postId}`)
    return response.data.data
  },

  async createPost(data: SocialPostCreate): Promise<SocialPost> {
    const response = await apiClient.post<ApiResponse<SocialPost>>('/social/posts', data)
    return response.data.data
  },

  async updatePost(postId: string, data: SocialPostUpdate): Promise<SocialPost> {
    const response = await apiClient.put<ApiResponse<SocialPost>>(`/social/posts/${postId}`, data)
    return response.data.data
  },

  async deletePost(postId: string): Promise<void> {
    await apiClient.delete(`/social/posts/${postId}`)
  },

  /**
   * Publish a post immediately.
   * @param postId - The post ID to publish
   * @param organizationId - For LinkedIn, the organization/company page ID to post to.
   *                         If not provided, posts to personal profile.
   */
  async publishPost(postId: string, organizationId?: string): Promise<SocialPost> {
    const response = await apiClient.post<ApiResponse<SocialPost>>(
      `/social/posts/${postId}/publish`,
      organizationId ? { organization_id: organizationId } : undefined
    )
    return response.data.data
  },

  async getScheduledPosts(limit?: number): Promise<SocialPost[]> {
    const response = await apiClient.get<ApiResponse<SocialPost[]>>('/social/posts/scheduled', {
      params: { limit }
    })
    return response.data.data
  },

  async getCalendarPosts(startDate: string, endDate: string): Promise<SocialPost[]> {
    const response = await apiClient.get<ApiResponse<SocialPost[]>>('/social/posts/calendar', {
      params: { start_date: startDate, end_date: endDate }
    })
    return response.data.data
  },

  // Analytics
  async getAnalytics(days?: number): Promise<SocialAnalytics> {
    const response = await apiClient.get<ApiResponse<SocialAnalytics>>('/social/analytics', {
      params: { days }
    })
    return response.data.data
  },

  async getEngagementTrends(days?: number): Promise<EngagementTrends> {
    const response = await apiClient.get<ApiResponse<EngagementTrends>>('/social/analytics/trends', {
      params: { days }
    })
    return response.data.data
  },

  async getBestPerformingPosts(days?: number, limit?: number): Promise<SocialPost[]> {
    const response = await apiClient.get<ApiResponse<SocialPost[]>>('/social/analytics/best-posts', {
      params: { days, limit }
    })
    return response.data.data
  },

  async getBestPostingTimes(platform?: string, days?: number): Promise<BestPostingTimes> {
    const response = await apiClient.get<ApiResponse<BestPostingTimes>>('/social/analytics/best-times', {
      params: { platform, days }
    })
    return response.data.data
  },

  // Queue Management
  async getPostQueue(): Promise<QueuedPost[]> {
    const response = await apiClient.get<ApiResponse<QueuedPost[]>>('/social/queue')
    return response.data.data
  },

  async reorderQueue(postIds: string[]): Promise<{ updated: number; start_time: string; end_time: string }> {
    const response = await apiClient.post<ApiResponse<{ updated: number; start_time: string; end_time: string }>>('/social/queue/reorder', {
      post_ids: postIds
    })
    return response.data.data
  },

  // Templates
  async listTemplates(category?: string): Promise<SocialTemplate[]> {
    const response = await apiClient.get<ApiResponse<SocialTemplate[]>>('/social/templates', {
      params: { category }
    })
    return response.data.data
  },

  async createTemplate(data: { name: string; content: string; platforms?: string[]; category?: string; tags?: string[] }): Promise<SocialTemplate> {
    const response = await apiClient.post<ApiResponse<SocialTemplate>>('/social/templates', data)
    return response.data.data
  },

  async deleteTemplate(templateId: string): Promise<void> {
    await apiClient.delete(`/social/templates/${templateId}`)
  },

  // AI Features
  async generatePost(data: GeneratePostRequest): Promise<{ content: string; platform: string; character_count: number; max_characters: number }> {
    const response = await apiClient.post<ApiResponse<{ content: string; platform: string; character_count: number; max_characters: number }>>('/social/ai/generate-post', data)
    return response.data.data
  },

  async improvePost(data: ImprovePostRequest): Promise<{ original: string; improved: string; improvement_type: string }> {
    const response = await apiClient.post<ApiResponse<{ original: string; improved: string; improvement_type: string }>>('/social/ai/improve-post', data)
    return response.data.data
  },

  // Approval Workflow
  async submitForApproval(postId: string, data: { approver_user_id?: string; send_teams_notification?: boolean }): Promise<SocialPost> {
    const response = await apiClient.post<ApiResponse<SocialPost>>(`/social/posts/${postId}/submit-for-approval`, data)
    return response.data.data
  },

  async approvePost(postId: string, notes?: string): Promise<SocialPost> {
    const response = await apiClient.post<ApiResponse<SocialPost>>(`/social/posts/${postId}/approve`, { notes })
    return response.data.data
  },

  async rejectPost(postId: string, notes?: string): Promise<SocialPost> {
    const response = await apiClient.post<ApiResponse<SocialPost>>(`/social/posts/${postId}/reject`, { notes })
    return response.data.data
  },

  async requestChanges(postId: string, notes: string): Promise<SocialPost> {
    const response = await apiClient.post<ApiResponse<SocialPost>>(`/social/posts/${postId}/request-changes`, { notes })
    return response.data.data
  },

  async listPendingApproval(): Promise<SocialPost[]> {
    const response = await apiClient.get<ApiResponse<SocialPost[]>>('/social/posts/pending-approval')
    return response.data.data
  },

  async updateProgress(postId: string, progress: number): Promise<SocialPost> {
    const response = await apiClient.post<ApiResponse<SocialPost>>(`/social/posts/${postId}/update-progress`, { progress })
    return response.data.data
  },

  async getOptions(): Promise<{
    statuses: string[]
    approval_statuses: string[]
    priorities: string[]
    categories: string[]
    platforms: { id: string; name: string; icon: string; color: string; max_chars: number }[]
  }> {
    const response = await apiClient.get<ApiResponse<{
      statuses: string[]
      approval_statuses: string[]
      priorities: string[]
      categories: string[]
      platforms: { id: string; name: string; icon: string; color: string; max_chars: number }[]
    }>>('/social/options')
    return response.data.data
  },
}
